import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {
  ErrorTypes,
  LoginFlowInterface,
  OnLoginFlowStateChangedCallback,
  StateTypes,
} from '@central-products/interface-login-flow';
import { StateType } from '@central-products/interface-session-manager';
import { LoginContainer } from './LoginContainer';
import { LoginFlowConfig, LoginFlowDependencies, OnActionRequested } from './types';
import './index.scss';

export class LoginFlow extends LoginFlowInterface {
  private readonly config: LoginFlowConfig;
  private readonly dependencies: LoginFlowDependencies;
  private state: StateTypes = StateTypes.NONE;
  private onStateChangedCallback?: OnLoginFlowStateChangedCallback;
  private domNode: HTMLElement | null = null;
  private isUserLoggedIn = false;

  constructor(config: LoginFlowConfig, dependencies: LoginFlowDependencies) {
    super();

    this.config = config;
    this.dependencies = dependencies;
  }

  public async init() {
    const { state: sessionState } = await this.dependencies['session-manager'].getState();
    this.isUserLoggedIn = sessionState === StateType.LOGGED_IN;

    this.dependencies['session-manager'].onStateChanged(({ state }) => {
      this.isUserLoggedIn = state === StateType.LOGGED_IN;

      if (this.isUserLoggedIn && this.state === StateTypes.FORM_VISIBLE) {
        this.cancel();
      }
    });
  }

  public async start() {
    if (this.state !== StateTypes.NONE) {
      throw { error: ErrorTypes.INVALID_STATE };
    }

    if (this.isUserLoggedIn) {
      throw { error: ErrorTypes.USER_ALREADY_LOGGED_IN };
    }

    window.performance?.mark?.('cp:login:start');
    this.render();
    this.changeState(StateTypes.FORM_VISIBLE);

    return {};
  }

  public async cancel() {
    if (this.state === StateTypes.NONE || this.domNode === null) {
      throw { error: ErrorTypes.INVALID_STATE };
    }

    ReactDOM.unmountComponentAtNode(this.domNode);
    document.body.removeChild(this.domNode);
    this.domNode = null;
    this.changeState(StateTypes.NONE);

    return {};
  }

  public async getState() {
    return { state: this.state };
  }

  public onStateChanged(callback: OnLoginFlowStateChangedCallback) {
    this.onStateChangedCallback = callback;
  }

  private render() {
    this.domNode = document.createElement('div');
    document.body.appendChild(this.domNode);

    const onClose = () => {
      this.cancel();
    };
    const onSubmit = () => {
      window.performance?.mark?.('cp:login:submit-start');
      this.changeState(StateTypes.AWAIT_LOGIN);
    };
    const onSubmitFailed = () => {
      window.performance?.mark?.('cp:login:submit-finish');
      this.changeState(StateTypes.FORM_VISIBLE);
    };
    const onSubmitSuccess = () => {
      window.performance?.mark?.('cp:login:submit-finish');
      this.cancel();
    };
    const onActionRequested: OnActionRequested = (eventId, context, isCloseRequired = true) => {
      this.dependencies['account-actions'].execute({
        eventId,
        context: { ...context, userFlow: 'login' },
      });

      if (isCloseRequired) {
        this.cancel();
      }
    };

    ReactDOM.render(
      <LoginContainer
        environment={this.config.environment}
        jurisdiction={this.config.jurisdiction}
        locale={this.config.locale}
        sessionManager={this.dependencies['session-manager']}
        webLoginPreferences={this.dependencies['web-login-preferences']}
        onClose={onClose}
        onSubmit={onSubmit}
        onSubmitSuccess={onSubmitSuccess}
        onSubmitFailed={onSubmitFailed}
        onActionRequested={onActionRequested}
        recaptchaService={this.dependencies['recaptcha-service']}
      />,
      this.domNode
    );
  }

  private changeState(state: StateTypes) {
    if (this.state === state) {
      return;
    }

    this.state = state;

    if (this.onStateChangedCallback) {
      this.onStateChangedCallback({ state });
    }
  }
}
